import React, { useEffect, useMemo, useState } from "react";
import {
  FaBook,
  FaBuilding,
  FaImage,
  FaListOl,
  FaMapMarkerAlt,
  FaSave,
  FaTimes,
  FaTrashAlt,
  FaUpload,
} from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../../../../../services/dojoPhotoServices";

import {
  DOJO_FALLBACK_IMAGE,
  DOJO_PLACEHOLDER_IMAGE,
  getDefaultDojoForm,
  mapDojoToForm,
} from "../utils/dojosSettingsUtils";

function DrawerField({ label, icon, children, hint }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <div className="relative">
        {icon ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            {icon}
          </span>
        ) : null}

        {children}
      </div>

      {hint ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export default function DojoFormDrawer({
  isOpen,
  mode,
  dojo,
  nextOrder,
  submitting,
  uploadProgress,
  onClose,
  onSubmit,
}) {
  const [formData, setFormData] = useState(getDefaultDojoForm(nextOrder));
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setSelectedFile(null);
    setFilePreview("");

    if (mode === "edit") {
      setFormData(mapDojoToForm(dojo, nextOrder));
    } else {
      setFormData(getDefaultDojoForm(nextOrder));
    }
  }, [isOpen, mode, dojo, nextOrder]);

  useEffect(() => {
    if (!selectedFile) {
      setFilePreview("");
      return undefined;
    }

    const url = URL.createObjectURL(selectedFile);
    setFilePreview(url);

    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const title = mode === "edit" ? "Modifier le dojo" : "Créer un dojo";
  const submitLabel =
    mode === "edit" ? "Enregistrer les modifications" : "Créer le dojo";

  const previewUrl = useMemo(() => {
    if (filePreview) return filePreview;

    return (
      toAbsolutePhotoUrl(formData.dojo_photoURL) ||
      DOJO_PLACEHOLDER_IMAGE ||
      DOJO_FALLBACK_IMAGE
    );
  }, [filePreview, formData.dojo_photoURL]);

  const canSubmit =
    String(formData.dojo_nom || "").trim() &&
    String(formData.dojo_discipline || "").trim() &&
    String(formData.dojo_adresse || "").trim() &&
    !submitting;

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const clearFile = () => {
    setSelectedFile(null);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    onSubmit?.({
      formData,
      file: selectedFile,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Dojo
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {title}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Renseignez les informations du lieu de pratique. Vous pouvez
              fournir une URL d’image ou téléverser directement une photo.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <div className="grid gap-5 lg:grid-cols-[220px,1fr]">
              <div>
                <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
                  <img
                    src={previewUrl}
                    alt={formData.dojo_nom || "Dojo"}
                    className="h-48 w-full object-cover"
                    referrerPolicy="no-referrer"
                    crossOrigin="anonymous"
                    onError={(event) => {
                      event.currentTarget.src = DOJO_FALLBACK_IMAGE;
                    }}
                  />
                </div>

                <p className="mt-2 text-center text-xs text-slate-500 dark:text-slate-400">
                  Aperçu de l’image du dojo.
                </p>
              </div>

              <div className="space-y-4">
                <DrawerField
                  label="Nom du dojo *"
                  icon={<FaBuilding />}
                >
                  <input
                    type="text"
                    name="dojo_nom"
                    value={formData.dojo_nom}
                    onChange={handleChange}
                    required
                    placeholder="Ex : Dojo municipal du Bouscat"
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </DrawerField>

                <DrawerField
                  label="Discipline *"
                  icon={<FaBook />}
                >
                  <input
                    type="text"
                    name="dojo_discipline"
                    value={formData.dojo_discipline}
                    onChange={handleChange}
                    required
                    placeholder="Judo, Ju-jitsu, Taïso..."
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </DrawerField>

                <DrawerField
                  label="Adresse *"
                  icon={<FaMapMarkerAlt />}
                >
                  <input
                    type="text"
                    name="dojo_adresse"
                    value={formData.dojo_adresse}
                    onChange={handleChange}
                    required
                    placeholder="Ex : 12 rue des Tatamis, 33110 Le Bouscat"
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </DrawerField>

                <DrawerField
                  label="Ordre d’affichage"
                  icon={<FaListOl />}
                  hint="Plus le nombre est petit, plus le dojo apparaît haut dans les listes publiques."
                >
                  <input
                    type="number"
                    name="dojo_ordre"
                    value={formData.dojo_ordre}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </DrawerField>
              </div>
            </div>

            <DrawerField
              label="URL de la photo"
              icon={<FaImage />}
              hint="Vous pouvez coller une URL externe, un chemin /uploads/... ou laisser vide si vous téléversez une image."
            >
              <input
                type="text"
                name="dojo_photoURL"
                value={formData.dojo_photoURL}
                onChange={handleChange}
                placeholder="https://... ou /uploads/..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              />
            </DrawerField>

            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
              <label className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                <FaUpload />
                Téléverser une photo
              </label>

              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleFileChange}
                disabled={submitting}
                className="block w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition file:mr-3 file:rounded-xl file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-bold file:text-indigo-700 hover:file:bg-indigo-100 focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:file:bg-indigo-500/10 dark:file:text-indigo-300"
              />

              {selectedFile ? (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
                  <span className="min-w-0 truncate">
                    {selectedFile.name} — {Math.round(selectedFile.size / 1024)} Ko
                  </span>

                  <button
                    type="button"
                    onClick={clearFile}
                    disabled={submitting}
                    className="inline-flex items-center gap-1 rounded-xl bg-rose-50 px-2 py-1 font-bold text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:bg-rose-500/10 dark:text-rose-300"
                  >
                    <FaTrashAlt />
                    Retirer
                  </button>
                </div>
              ) : null}

              <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                Formats acceptés : PNG, JPG, WEBP. Taille maximale : 10 Mo. Si
                une URL et un fichier sont fournis, le fichier téléversé sera
                prioritaire.
              </p>

              {submitting && selectedFile ? (
                <div className="mt-4">
                  <div className="mb-1 flex items-center justify-between text-xs font-bold text-slate-600 dark:text-slate-300">
                    <span>Transfert en cours</span>
                    <span>{uploadProgress}%</span>
                  </div>

                  <div className="h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#013E8E] to-[#4CA82F] transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {submitting ? "Enregistrement..." : submitLabel}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
}